import { Card, useAlertEmit } from "@fpsoftware/mvp";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { type Control, useController, useForm } from "react-hook-form";
import { TbDeviceFloppy } from "react-icons/tb";
import { CompanyPatchBodySchema, ProfilePatchBodySchema } from "shared";
import z from "zod";
import { Api } from "@/api.config";
import { LogoUpload } from "@/auth/LogoUpload";
import { useProfile } from "@/auth/useProfile";
import { Button } from "@/components/Button";
import { FormGroup } from "@/components/form/FormGroup";
import { Input } from "@/components/form/Input";
import { TextField } from "@/components/form/TextField";
import { QueryKeys } from "@/LinkGen";
import { AppPage } from "@/layout/AppLayout";
import { CompanyFeesForm } from "./CompanyFeesForm";
import styles from "./PageSettings.module.css";

const CompanyFormSchema = CompanyPatchBodySchema.extend({
  logo: z.custom<File>().nullable(),
});

type CompanyFormValues = z.infer<typeof CompanyFormSchema>;

function ColorField(props: {
  control: Control<CompanyFormValues>;
  name: "color";
}) {
  const { field, fieldState } = useController({
    control: props.control,
    name: props.name,
  });

  return (
    <FormGroup
      label="Brand color (Hex code)"
      htmlFor="color"
      error={fieldState.error?.message}
    >
      <div className={styles.colorField}>
        <Input
          id="color"
          placeholder="#000000"
          {...field}
          value={field.value ?? ""}
        />
        <input
          type="color"
          value={field.value || "#000000"}
          onChange={(e) => field.onChange(e.target.value)}
          className={styles.colorPicker}
        />
      </div>
    </FormGroup>
  );
}

function ProfileForm() {
  const { profile } = useProfile();
  const queryClient = useQueryClient();
  const emit = useAlertEmit();

  const { handleSubmit, formState, control } = useForm({
    resolver: zodResolver(ProfilePatchBodySchema),
    defaultValues: {
      firstName: profile.firstName,
      lastName: profile.lastName,
      phone: profile.phone,
      nmls: profile.nmls,
      licenseNumber: profile.licenseNumber,
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    await Api.ProfilePatch(values);
    await queryClient.invalidateQueries({ queryKey: QueryKeys.Profile() });
    emit({ type: "success", content: "Profile updated successfully." });
  });

  return (
    <Card>
      <form onSubmit={onSubmit}>
        <Card.Header>Your profile</Card.Header>
        <Card.Body>
          <div className={styles.form}>
            <TextField
              label="First name"
              control={control}
              name="firstName"
              required
            />
            <TextField
              label="Last name"
              control={control}
              name="lastName"
              required
            />
            <TextField label="Phone" control={control} name="phone" required />
            <TextField
              label="NMLS number"
              control={control}
              name="nmls"
              required
            />
            <TextField
              label="License number"
              control={control}
              name="licenseNumber"
              required
            />
          </div>
        </Card.Body>
        <Card.Footer>
          <div className={styles.actions}>
            <Button
              icon={TbDeviceFloppy}
              type="submit"
              loading={formState.isSubmitting}
            >
              Save
            </Button>
          </div>
        </Card.Footer>
      </form>
    </Card>
  );
}

function CompanyForm() {
  const { profile } = useProfile();
  const queryClient = useQueryClient();
  const emit = useAlertEmit();

  const { handleSubmit, formState, control } = useForm({
    resolver: zodResolver(CompanyFormSchema),
    defaultValues: {
      name: profile.companyName,
      nmls: profile.companyNmls,
      address: profile.companyAddress,
      color: profile.companyColor,
      logo: null as File | null,
    },
  });

  const onSubmit = handleSubmit(async ({ logo, ...values }) => {
    await Api.CompanyPatch(values, logo);
    await queryClient.invalidateQueries({ queryKey: QueryKeys.Profile() });
    emit({ type: "success", content: "Company profile updated successfully." });
  });

  return (
    <Card>
      <form onSubmit={onSubmit}>
        <Card.Header>Company profile</Card.Header>
        <Card.Body>
          <div className={styles.form}>
            <TextField
              label="Company name"
              control={control}
              name="name"
              required
            />
            <TextField
              label="Company NMLS"
              control={control}
              name="nmls"
              required
            />
            <TextField
              label="Company address"
              control={control}
              name="address"
              required
            />
            <ColorField control={control} name="color" />
            <FormGroup label="Company logo" htmlFor="company-logo">
              <LogoUpload
                id="company-logo"
                control={control}
                name="logo"
                initialPreviewUrl={`data:;base64,${profile.companyLogo}`}
              />
            </FormGroup>
          </div>
        </Card.Body>
        <Card.Footer>
          <div className={styles.actions}>
            <Button
              icon={TbDeviceFloppy}
              type="submit"
              loading={formState.isSubmitting}
            >
              Save
            </Button>
          </div>
        </Card.Footer>
      </form>
    </Card>
  );
}

// TODO: temporary — remove. Crashes the page to test error handling.
function CrashButton() {
  const [crash, setCrash] = useState(false);

  if (crash) {
    throw new Error("Temporary crash button triggered");
  }

  return <Button onClick={() => setCrash(true)}>Crash the page (temp)</Button>;
}

export function PageSettings() {
  return (
    <AppPage title="Settings" className={styles.page}>
      <ProfileForm />
      <CompanyForm />
      <CompanyFeesForm />
      <CrashButton />
    </AppPage>
  );
}
